/**
 * BusinessDashboard — vendor-context view rendered on AccountPage
 * whenever active_context === "business".
 *
 * Four KPI tiles: Orders today · Revenue today · Points redeemed
 * · Ready-for-handover. Below: a 7-day revenue sparkline and a
 * top-3-items list. Everything comes from
 * `GET /api/vendor/dashboard-stats` (owner-scoped by business_id).
 *
 * If the endpoint 403s (no business), we hide the whole panel — a
 * safety net rather than an error surface.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag, DollarSign, Gift, ClipboardCheck,
  ArrowRight, TrendingUp, ChefHat,
} from "lucide-react";
import { api } from "@/lib/api";

function fmtAud(cents) {
  const n = Number(cents || 0) / 100;
  return new Intl.NumberFormat("en-AU",
                                { style: "currency", currency: "AUD" })
              .format(n);
}
function fmtInt(n) { return new Intl.NumberFormat("en-AU").format(n || 0); }

/** Tiny inline SVG sparkline — no chart library, no bundle bloat. */
function Sparkline({ data, height = 60, stroke = "#5A7247" }) {
  const points = useMemo(() => {
    const values = data.map((d) => d.cents || 0);
    const max = Math.max(1, ...values);
    const step = 100 / Math.max(1, values.length - 1);
    return values.map((v, i) => `${i * step},${100 - (v / max) * 90 - 5}`)
                  .join(" ");
  }, [data]);
  return (
    <svg
      data-testid="business-dashboard-sparkline"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
    >
      <polyline
        fill="none" stroke={stroke} strokeWidth="1.8"
        strokeLinejoin="round" strokeLinecap="round"
        points={points}
      />
    </svg>
  );
}

function StatTile({ label, value, sub, icon: Icon, tone = "green", testid }) {
  const toneMap = {
    green:  "bg-[#F1F4EA] text-[#5A7247] border-[#D8E1CA]",
    amber:  "bg-[#FBF3D9] text-[#8B6E1E] border-[#F1E4A1]",
    ember:  "bg-[#FBE9E1] text-[#B04C36] border-[#F0C2AF]",
    slate:  "bg-[#F3F0EA] text-[#3C423F] border-[#E2DDD1]",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border ${toneMap[tone] || toneMap.slate}
                  bg-white dark:bg-stone-800/60
                  border-[#E8E5DF] dark:border-stone-700
                  p-4 md:p-5 flex flex-col gap-2`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-semibold text-stone-600 dark:text-stone-300">
        <Icon className="w-3.5 h-3.5" strokeWidth={2} />
        {label}
      </div>
      <div className="font-serif-display text-3xl md:text-4xl text-[#121614] dark:text-stone-100 leading-none">
        {value}
      </div>
      {sub && (
        <div className="text-xs text-stone-500 dark:text-stone-400">
          {sub}
        </div>
      )}
    </div>
  );
}

export default function BusinessDashboard() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | hidden

  useEffect(() => {
    let cancelled = false;
    api.get("/vendor/dashboard-stats")
       .then((r) => { if (!cancelled) { setData(r.data); setState("ready"); } })
       .catch((e) => {
         if (cancelled) return;
         // 403 → no business_id on account, silently hide.
         if (e?.response?.status === 403 || e?.response?.status === 404) {
           setState("hidden");
         } else {
           setState("ready");     // show empty state instead of hiding
           setData(null);
         }
       });
    return () => { cancelled = true; };
  }, []);

  if (state === "hidden") return null;
  if (state === "loading") {
    return (
      <div
        data-testid="business-dashboard-loading"
        className="rounded-2xl border border-dashed border-[#E8E5DF] p-6 text-[#5C625E]"
      >
        Loading business dashboard…
      </div>
    );
  }
  if (!data) {
    return (
      <div
        data-testid="business-dashboard-empty"
        className="rounded-2xl border border-dashed border-[#E8E5DF] p-6 text-[#5C625E]"
      >
        No dashboard data yet — take your first order to light this
        page up.
      </div>
    );
  }

  return (
    <div data-testid="business-dashboard" className="space-y-6">
      {/* Header row */}
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
            Business dashboard
          </p>
          <h2 className="font-serif-display text-2xl mt-1">
            {data.business_name || "Your business"}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/vendor/orders"
            data-testid="business-dashboard-orders-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5A7247] hover:text-[#4A5F3A]"
          >
            Live orders <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
          <Link
            to="/vendor/ledger"
            data-testid="business-dashboard-ledger-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5A7247] hover:text-[#4A5F3A]"
          >
            Ledger <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
          <Link
            to="/vendor/points"
            data-testid="business-dashboard-points-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5A7247] hover:text-[#4A5F3A]"
          >
            Points <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatTile
          testid="business-stat-orders-today"
          label="Orders today"
          value={fmtInt(data.orders_today)}
          sub={`${fmtInt(data.orders_month)} in the last 30 days`}
          icon={ShoppingBag}
          tone="green"
        />
        <StatTile
          testid="business-stat-revenue-today"
          label="Revenue today"
          value={fmtAud(data.revenue_today_cents)}
          sub={`${fmtAud(data.revenue_month_cents)} last 30 days`}
          icon={DollarSign}
          tone="amber"
        />
        <StatTile
          testid="business-stat-points-redeemed"
          label="Points redeemed today"
          value={fmtInt(data.points_redeemed_today)}
          sub={`${fmtInt(data.points_redeemed_month)} pts this month`}
          icon={Gift}
          tone="ember"
        />
        <StatTile
          testid="business-stat-ready-count"
          label="Ready for handover"
          value={fmtInt(data.pending_ready_count)}
          sub={`${fmtInt(data.in_prep_count)} still preparing`}
          icon={ClipboardCheck}
          tone="slate"
        />
      </div>

      {/* Trend + Top items */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-white dark:bg-stone-800/60 border border-[#E8E5DF] dark:border-stone-700 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-semibold text-stone-600 dark:text-stone-300">
            <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} />
            Revenue · last 7 days
          </div>
          <div className="mt-3">
            <Sparkline data={data.revenue_trend_7d || []} />
          </div>
          <div className="mt-2 grid grid-cols-7 gap-0.5 text-[10px] text-stone-500 dark:text-stone-400">
            {(data.revenue_trend_7d || []).map((d) => (
              <span key={d.d} className="text-center truncate">
                {d.d.slice(5)}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white dark:bg-stone-800/60 border border-[#E8E5DF] dark:border-stone-700 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-semibold text-stone-600 dark:text-stone-300">
            <ChefHat className="w-3.5 h-3.5" strokeWidth={2} />
            Top items · 30d
          </div>
          {(data.top_items || []).length === 0 ? (
            <p className="mt-3 text-sm text-stone-500 dark:text-stone-400">
              No items sold yet.
            </p>
          ) : (
            <ol className="mt-3 space-y-2">
              {(data.top_items || []).map((it, i) => (
                <li
                  key={it.name}
                  data-testid={`business-top-item-${i}`}
                  className="flex items-center gap-2 text-sm"
                >
                  <span className="w-5 h-5 rounded-full bg-[#F1F4EA] text-[#5A7247] text-[11px] font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="flex-1 truncate text-[#121614] dark:text-stone-100">
                    {it.name}
                  </span>
                  <span className="text-stone-500 dark:text-stone-400 tabular-nums">
                    {fmtInt(it.qty)}× · {fmtAud(it.cents)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
